/**
 * TicketsAdminView — Supervisión de Tickets de descuento (Ticket Club). Solo admins.
 * Tabla agente × nivel (emitidos) + estados + tasa de canje global, con selector de
 * rango, y editor de la config de duraciones (presets + tope custom).
 * Sesión por cookie httpOnly same-origin (patrón F2): fetch sin header Authorization.
 */

import { useState, useEffect, useCallback } from 'react'

const NIVELES = [1, 2, 3, 4, 5, 6]
const RANGOS = [7, 30, 90]

const ESTADO_LABEL = { active: 'Vigente', redeemed: 'Canjeado', expired: 'Vencido', cancelled: 'Anulado' }

function duracionLabel(d) {
  if (!d) return '—'
  return `${d} día${d === 1 ? '' : 's'}`
}

// Fila de agente + detalle desplegable (a quién, cuándo, duración, estado, orden).
function FilaAgente({ a, expandido, tickets, onToggle }) {
  const fecha = (iso) => new Date(iso).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  return (
    <>
      <tr onClick={onToggle} className="cursor-pointer hover:bg-gray-50 dark:hover:bg-surface-dark-elev">
        <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">
          <span className={`inline-block mr-1.5 text-gray-400 transition-transform ${expandido ? 'rotate-90' : ''}`}>▸</span>
          {a.nombre}
        </td>
        {NIVELES.map((n) => (
          <td key={n} className="px-2 py-2 text-center text-gray-600 dark:text-gray-300">{a.niveles[n] || ''}</td>
        ))}
        <td className="px-3 py-2 text-center font-semibold">{a.emitidos}</td>
        <td className="px-3 py-2 text-center text-green-700 dark:text-green-400">{a.activos || ''}</td>
        <td className="px-3 py-2 text-center text-blue-700 dark:text-blue-400">{a.canjeados || ''}</td>
      </tr>
      {expandido && (
        <tr>
          <td colSpan={10} className="px-4 pb-3 pt-1 bg-gray-50/60 dark:bg-surface-dark-elev/60">
            {!tickets && <div className="text-xs text-gray-400 py-2">Cargando…</div>}
            {tickets && tickets.length === 0 && <div className="text-xs text-gray-400 py-2">Sin tickets en el rango.</div>}
            {tickets && tickets.length > 0 && (
              <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-gray-500">
                  <tr>
                    <th className="text-left py-1 pr-2">Prospecto</th>
                    <th className="text-left py-1 pr-2">Código</th>
                    <th className="py-1 pr-2">Nivel</th>
                    <th className="py-1 pr-2">Duración</th>
                    <th className="text-left py-1 pr-2">Emitido</th>
                    <th className="text-left py-1">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-surface-dark-border">
                  {tickets.map((t) => (
                    <tr key={t.id}>
                      <td className="py-1.5 pr-2">
                        {t.prospecto_nombre ? <span className="font-medium">{t.prospecto_nombre}</span> : null}
                        <span className="text-gray-400"> {t.phone}</span>
                      </td>
                      <td className="py-1.5 pr-2 font-mono">{t.code}</td>
                      <td className="py-1.5 pr-2 text-center">N{t.level}</td>
                      <td className="py-1.5 pr-2 text-center">{duracionLabel(t.duracion_dias)}</td>
                      <td className="py-1.5 pr-2">{fecha(t.created_at)}</td>
                      <td className="py-1.5">
                        {ESTADO_LABEL[t.status] || t.status}
                        {t.status === 'active' ? ` · vence ${fecha(t.expires_at)}` : ''}
                        {t.status === 'redeemed' && t.woo_order_id ? ` · orden #${t.woo_order_id}` : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  )
}

export default function TicketsAdminView() {
  const [days, setDays] = useState(30)
  const [stats, setStats] = useState(null)   // { porAgente:[{created_by,level,status,total}], totales:{emitidos,canjeados} }
  const [config, setConfig] = useState(null) // { presetDays, maxCustomDays }
  const [presetsTexto, setPresetsTexto] = useState('')
  const [maxTexto, setMaxTexto] = useState('')
  const [guardando, setGuardando] = useState(false)
  const [aviso, setAviso] = useState('')

  const cargarStats = useCallback(() => {
    fetch(`/api/crm-discount-tickets/stats?days=${days}`)
      .then((r) => r.json())
      .then((d) => { if (d.success) setStats(d) })
      .catch(() => {})
  }, [days])

  useEffect(() => { cargarStats() }, [cargarStats])

  useEffect(() => {
    fetch('/api/crm-discount-tickets/config')
      .then((r) => r.json())
      .then((d) => {
        if (d.success) {
          setConfig(d.config)
          setPresetsTexto((d.config.presetDays || []).join(', '))
          setMaxTexto(String(d.config.maxCustomDays || 14))
        }
      })
      .catch(() => {})
  }, [])

  async function guardarConfig() {
    setGuardando(true)
    setAviso('')
    try {
      const r = await fetch('/api/crm-discount-tickets/admin/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          presetDays: presetsTexto.split(',').map((s) => parseInt(s.trim(), 10)).filter(Boolean),
          maxCustomDays: parseInt(maxTexto, 10),
        }),
      })
      const d = await r.json()
      if (!r.ok) { setAviso(d.error || 'No se pudo guardar'); return }
      setConfig(d.config)
      setPresetsTexto((d.config.presetDays || []).join(', '))
      setMaxTexto(String(d.config.maxCustomDays))
      setAviso('Guardado ✓')
      setTimeout(() => setAviso(''), 2000)
    } catch {
      setAviso('Sin conexión')
    } finally {
      setGuardando(false)
    }
  }

  // Detalle desplegable por agente (feedback de Giancarlo): a quién, cuándo, duración.
  const [expandido, setExpandido] = useState(null)   // created_by del agente expandido | null
  const [detalle, setDetalle] = useState({})         // { [created_by]: tickets[] }

  async function toggleDetalle(agente) {
    if (expandido === agente) { setExpandido(null); return }
    setExpandido(agente)
    if (!detalle[agente]) {
      try {
        const r = await fetch(`/api/crm-discount-tickets/stats/agent?agent=${encodeURIComponent(agente)}&days=${days}`)
        const d = await r.json()
        if (d.success) setDetalle((prev) => ({ ...prev, [agente]: d.tickets }))
      } catch { /* fila queda en "Cargando…" y el admin reintenta */ }
    }
  }

  // Al cambiar el rango, el detalle cacheado queda obsoleto → limpiar.
  useEffect(() => { setDetalle({}); setExpandido(null) }, [days])

  // Pivot: filas crudas {created_by, agente_nombre, level, status, total} → por agente
  // {nombre (el de login del CRM), niveles:{1..6}, emitidos, canjeados, activos}
  const porAgente = (() => {
    if (!stats) return []
    const mapa = new Map()
    for (const f of stats.porAgente || []) {
      if (!mapa.has(f.created_by)) {
        mapa.set(f.created_by, { agente: f.created_by, nombre: f.agente_nombre || f.created_by, niveles: {}, emitidos: 0, canjeados: 0, activos: 0 })
      }
      const a = mapa.get(f.created_by)
      a.niveles[f.level] = (a.niveles[f.level] || 0) + f.total
      a.emitidos += f.total
      if (f.status === 'redeemed') a.canjeados += f.total
      if (f.status === 'active') a.activos += f.total
    }
    return Array.from(mapa.values()).sort((x, y) => y.emitidos - x.emitidos)
  })()

  const tot = stats?.totales
  const tasa = tot && tot.emitidos > 0 ? Math.round((tot.canjeados / tot.emitidos) * 100) : 0

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">🎟️ Supervisión de tickets</h2>
        <div className="flex gap-1.5">
          {RANGOS.map((r) => (
            <button
              key={r}
              onClick={() => setDays(r)}
              className={`text-sm px-3 py-1 rounded-lg border ${
                days === r
                  ? 'bg-primary-500 text-white border-primary-500'
                  : 'border-gray-300 dark:border-surface-dark-border text-gray-600 dark:text-gray-300'
              }`}
            >
              {r} días
            </button>
          ))}
        </div>
      </div>

      {/* Totales */}
      <div className="grid grid-cols-3 gap-4">
        <div className="p-4 rounded-xl bg-gray-50 dark:bg-surface-dark-elev">
          <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{tot?.emitidos ?? '—'}</div>
          <div className="text-sm text-gray-500">Tickets emitidos</div>
        </div>
        <div className="p-4 rounded-xl bg-green-50 dark:bg-green-900/20">
          <div className="text-3xl font-bold text-green-700 dark:text-green-400">{tot?.canjeados ?? '—'}</div>
          <div className="text-sm text-gray-500">Canjeados (ventas)</div>
        </div>
        <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20">
          <div className="text-3xl font-bold text-blue-700 dark:text-blue-400">{tasa}%</div>
          <div className="text-sm text-gray-500">Tasa de canje</div>
        </div>
      </div>

      {/* Tabla agente × nivel */}
      <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-surface-dark-border">
        {/* table-fixed + anchos explícitos: sin esto, el sub-desglose expandido ensancha
            la tabla exterior y TODO el layout "salta" (feedback de Giancarlo). El detalle
            largo scrollea DENTRO de su fila (overflow en FilaAgente), no empuja columnas. */}
        <table className="w-full text-sm table-fixed">
          <thead className="bg-gray-50 dark:bg-surface-dark-elev text-gray-600 dark:text-gray-300">
            <tr>
              <th className="text-left px-3 py-2 w-[22%]">Agente</th>
              {NIVELES.map((n) => <th key={n} className="px-2 py-2 w-[6%]">N{n}</th>)}
              <th className="px-3 py-2 w-[14%]">Emitidos</th>
              <th className="px-3 py-2 w-[14%]">Vigentes</th>
              <th className="px-3 py-2 w-[14%]">Canjeados</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-surface-dark-border">
            {porAgente.length === 0 && (
              <tr><td colSpan={10} className="px-3 py-6 text-center text-gray-400">Sin tickets en el rango.</td></tr>
            )}
            {porAgente.map((a) => (
              <FilaAgente
                key={a.agente}
                a={a}
                expandido={expandido === a.agente}
                tickets={detalle[a.agente]}
                onToggle={() => toggleDetalle(a.agente)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Config de duraciones */}
      <div className="p-4 rounded-xl border border-gray-200 dark:border-surface-dark-border space-y-3">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100">Duraciones (config global)</h3>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm text-gray-600 dark:text-gray-300">
            Presets (días, separados por coma)
            <input
              value={presetsTexto}
              onChange={(e) => setPresetsTexto(e.target.value)}
              className="block mt-1 w-56 px-2 py-1.5 rounded border border-gray-300 dark:border-surface-dark-border bg-white dark:bg-surface-dark text-sm"
            />
          </label>
          <label className="text-sm text-gray-600 dark:text-gray-300">
            Tope custom (días, máx 14)
            <input
              type="number" min="1" max="14"
              value={maxTexto}
              onChange={(e) => setMaxTexto(e.target.value)}
              className="block mt-1 w-32 px-2 py-1.5 rounded border border-gray-300 dark:border-surface-dark-border bg-white dark:bg-surface-dark text-sm"
            />
          </label>
          <button
            onClick={guardarConfig}
            disabled={guardando}
            className="px-4 py-1.5 rounded-lg bg-primary-500 text-white text-sm font-medium disabled:opacity-50"
          >
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
          {aviso && <span className="text-sm text-gray-500">{aviso}</span>}
        </div>
        {config && (
          <p className="text-xs text-gray-400">
            El tope duro del negocio es 14 días; el backend y la BD lo garantizan aunque acá se intente más. Los tickets vencen a las 23:59 hora Chile del día objetivo, no a las N×24 h de emitidos.
          </p>
        )}
      </div>
    </div>
  )
}
